import { db, getCurrentUserId } from './firebase';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';

// Firestore stores a conversation's messages under `conversation` (see the doc created in
// firebase.js). This module used to read `history`, which has never been a field on these
// documents, so the message-count check below silently failed for every test ever taken.
//
// That was masked while results were also mirrored to the Realtime Database — this function
// tried RTDB first and usually got an answer there. The Canada migration removed RTDB, which
// left this broken path as the only one, so `fetchLastPlacementTestResult` began returning
// null unconditionally: no inline score after a test, and a placement-test prompt that
// re-fired every session because the app could never see a completed test (MAD-788).
const MESSAGES_FIELD = 'conversation';

// A test shorter than this didn't gather enough speech to be worth reporting.
const MIN_MESSAGES_FOR_VALID_TEST = 4;

/**
 * Fetches the most recent test result for the current user.
 * @param {string} activityId - The activity ID to filter by (e.g. 'placement-test', 'exam-tcf-practice')
 * @returns {Promise<Object|null>} The most recent test result or null if none found
 */
export const fetchLastPlacementTestResult = async (activityId = 'placement-test') => {
  const userId = getCurrentUserId();
  if (!userId) {
    return null;
  }

  try {
    return await fetchFromConversations(userId, activityId);
  } catch (error) {
    console.error('Error fetching placement test results:', error);
    return null;
  }
};

/**
 * Fetches test results from the conversations collection
 * @param {string} userId - The user ID
 * @param {string} activityId - The activity ID to filter by
 * @returns {Promise<Object|null>} The most recent test result or null if none found
 */
const fetchFromConversations = async (userId, activityId = 'placement-test') => {
  try {
    const q = query(
      collection(db, 'conversations'),
      where('userId', '==', userId),
      where('selectedActivity', '==', activityId),
      orderBy('createdAt', 'desc'),
      limit(5) // Fetch top 5 to find valid tests with enough messages
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    let validDoc = null;
    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      const estimatedLevel = data.estimated_level || (data.analysis && data.analysis.estimated_level);

      // Check the conversation gathered enough speech to be a valid placement test
      const messageCount = data[MESSAGES_FIELD]?.length || 0;
      const isValidTest = messageCount >= MIN_MESSAGES_FOR_VALID_TEST;

      if (estimatedLevel && estimatedLevel.score_average && isValidTest) {
        validDoc = {
          doc,
          data,
          estimatedLevel
        };
        break;
      }
    }

    if (!validDoc) {
      return null;
    }

    const { doc, data, estimatedLevel } = validDoc;

    const result = {
      id: doc.id,
      date: data.createdAt?.toDate() || null, // Handle missing createdAt
      estimated_level: estimatedLevel,
      score: estimatedLevel.score_average || 0
    };
    return result;
  } catch (error) {
    console.error('Error fetching from conversations:', error);
    return null;
  }
};